import Controller, { OnAfterFetch, OnBeforeSubmit, OnClear, OnClick, OnMount, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { ClickEvent, FetchEvent, MountEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { Inject } from '@odinjs/odin';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { AutocompleteAPI, DeckAPI, DeckValue, FilterAPI, PanelAPI, ToastService } from '@philips-internal/oswald-ui-components';
import BaseController from '../../BaseController';
import { DateFilterUtils } from '../../../utils/date-filter-utils';
import { ApplicationRoute } from '../../../constants/routes';
import InformationBarUtils from '../../commons/header/information-bar-header-utils';
import { Value } from '@philips-internal/oswald-ui-core';

import ToastType from '@philips-internal/oswald-ui-constants/lib/toast-type';
import Placement from '@philips-internal/oswald-ui-constants/lib/placement';
import ToastLevel from '@philips-internal/oswald-ui-constants/lib/toast-level';
import { DiagnosisAliases } from '../../../codegen/constants/aliases/diagnosis-aliases';
import moment from 'moment';
import { DeckContext } from '../../commons/Deck/deck-service';
import DiagnosisService, { DiagnosisContext } from '../diagnosis-service';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { FragmentNames } from '../../../codegen';
import { ItemPepType, RulesItemConfiguration } from '../../../constants/utils-pep';
import { Endpoint } from '../../../constants/endpoints';
import { YesNoType } from '../../../utils/enum-utils';

enum StatusType {
  ACTIVE = 'A'
}

@Controller({ component: DiagnosisAliases.LAYOUT_DIAGNOSIS, fragment: FragmentNames.DIAGNOSIS })
export default class DiagnosisController extends BaseController {

  @Inject(DiagnosisAliases.FILTER_DIAGNOSIS)
  private filterApi!: FilterAPI;

  @Inject(DiagnosisAliases.DECK_DIAGNOSIS)
  private deckDiagnosisApi!: DeckAPI;

  @Inject(DiagnosisAliases.PANEL_GLOBAL_ACTIONS)
  private panelGlobalActionsApi!: PanelAPI

  @OnClear({ target: DiagnosisAliases.FILTER_DIAGNOSIS })
  handleFilterClear(): void {
    this.filterApi.clear();
  }

  @OnMount({ target: DiagnosisAliases.FILTER_DIAGNOSIS })
  handleFilterMount(): void {
    const hasState = this.filterApi.restoreState(true);

    if (hasState) {
      DiagnosisContext.setInternalNavigation(false);
      this.filterApi.submit();
      return;
    }

    const data = this.getinitialFilterValue();
    this.filterApi.setData(data as Record<string, Value>);
  }

  @OnMount({ target: DiagnosisAliases.AUTOCOMPLETE_TYPE_PRESENTATION})
  handleMountInstallationSuccess(event: MountEvent): void {
    const fieldApi = event.target as AutocompleteAPI;
    const data = [
      {
        description: MessageService.translate('label.all-encounter'),
        id: 1,
      },
      {
        description: MessageService.translate('label.all-patient'),
        id: 2,
      }
    ]
    fieldApi.setData(data);
    fieldApi.setValue(data[0]);
  }

  @OnUnmount(DiagnosisAliases.FILTER_DIAGNOSIS)
  handleFilterUnmount(event: MountEvent): void {
    if (DiagnosisContext.isInternalNavigation()) {
      const filterDiagnosis = event.target as FilterAPI;
      filterDiagnosis.persistState();
    }
  }

  @OnMount({ target: DiagnosisAliases.LAYOUT_DIAGNOSIS })
  handleMount(): void {
    InformationBarUtils.collapsedBar = true;

    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    this.context.set({ encounterId, patientId });

    this.checkItemEditPermission()
      .finally(() => {
        const allowInfo = InformationBarUtils.allowInfo && DiagnosisService.isAllowEdit;
        this.panelGlobalActionsApi.setVisible(allowInfo);
      });

  }

  @OnUnmount({ target: DiagnosisAliases.LAYOUT_DIAGNOSIS })
  handleUnmount(): void {
    InformationBarUtils.collapsedBar = false;
  }

  @OnClick({ target: DiagnosisAliases.DIAGNOSIS_CARD })
  handleCardClick(event: ClickEvent): void {
    DiagnosisContext.setInternalNavigation(true);

    const { sequenceNumber, status } = event.value;
    const encounterId = this.context.get('encounterId') as string;
    const allowInfo = InformationBarUtils.allowInfo && DiagnosisService.isAllowEdit;
    const accessViewer = ((status === StatusType.ACTIVE) || (!allowInfo));
    const route = accessViewer ? ApplicationRoute.DIAGNOSIS_VIEWER : ApplicationRoute.DIAGNOSIS_EDIT;
    const params = accessViewer ? { encounterId, sequenceNumber } : { encounterId, sequenceNumber, status };

    applicationContext.redirect(route, params);
  }

  @OnClick({ target: DiagnosisAliases.BUTTON_ADD_DIAGNOSIS })
  handleAddClinicalNotes(): void {
    applicationContext.redirect(ApplicationRoute.DIAGNOSIS_CREATE);
  }

  getinitialFilterValue(): Record<string, Value> {
    const { lastMonth, currentDate } = DateFilterUtils.getDateRange();

    const apresentationExams = {
      description: MessageService.translate('label.all-encounter'),
      id: 1
    };

    return {
      endDate: currentDate,
      presentationType: apresentationExams,
      startDate: lastMonth
    }
  }

  @OnBeforeSubmit({ target: DiagnosisAliases.FILTER_DIAGNOSIS })
  handleBeforeSubmit(event: SubmitEvent) : void {
    const endDate = moment(event.record.dtFinal)
    const startDate = moment(event.record.dtInicial)
    if(endDate.diff(startDate, 'days') > 90) {
      this.showToast();
    }
  }

  @OnAfterFetch(DiagnosisAliases.FETCH_DIAGNOSIS)
  handleFetchDiagnosis(event: FetchEvent): void {
    const data = event.data as Record<string, string>[];
    const deckValue = data.map(item => ({
      ...item
    })) as DeckValue[];
    DeckContext.setParamActivate(deckValue);
    this.deckDiagnosisApi.setData(deckValue);
  }

  showToast(): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'mensageCautionDiagnosis',
      level: ToastLevel.CAUTION,
      message: MessageService.translate('text.loss-performance'),
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  private checkItemEditPermission(): Promise<void> {
    const itemType = ItemPepType.DIAGNOSIS;
    return HttpService.get(Endpoint.PEP_CAN_EDIT_ITEM, { itemType })
      .then(response => {
        const configuration = response.body as RulesItemConfiguration;
        const isAllowEdit = configuration?.allowEditing === YesNoType.YES;
        DiagnosisService.isAllowEdit = isAllowEdit;
      })
  }
}
